<template>
  <div class="matching-order-page">
    <div class="breadcrumb">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item>订单管理</el-breadcrumb-item>
        <el-breadcrumb-item>匹配订单</el-breadcrumb-item>
      </el-breadcrumb>
    </div>

    <el-card class="filter-card" shadow="never">
      <div class="filter-row">
        <span class="filter-label">订单状态</span>
        <el-radio-group v-model="currentStatus" size="small" @change="getList(true)">
          <el-radio-button v-for="item in statusOptions" :key="String(item.value)" :label="item.value">
            {{ item.label }}
          </el-radio-button>
        </el-radio-group>
      </div>
    </el-card>

    <el-card class="table-card" shadow="never">
      <div class="table-header">
        <div class="card-title">本人匹配订单 <span>共 {{ total }} 条</span></div>
        <el-button type="primary" size="small" icon="el-icon-refresh" :loading="listLoading" @click="getList()">刷新</el-button>
      </div>

      <el-table
        v-loading="listLoading"
        :data="tableData"
        row-key="id"
        border
        empty-text="暂无匹配订单"
        class="order-table"
        :header-cell-style="headerStyle"
        :cell-style="cellStyle"
      >
        <el-table-column fixed="left" prop="order_no" label="订单编号" width="150" align="center">
          <template slot-scope="scope">
            <div class="order-identity">
              <span class="order-number">{{ scope.row._view.orderNo }}</span>
              <small>{{ scope.row._view.createTime }}</small>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="状态/类型" width="145" align="center">
          <template slot-scope="scope">
            <div class="status-cell">
              <el-tag
                :type="statusTagType(scope.row._view.status.type)"
                :class="['status-tag', { 'status-tag-received': scope.row._view.status.label === '被接单' }]"
                size="small"
              >
                {{ scope.row._view.status.label }}
              </el-tag>
              <el-tag
                v-for="item in scope.row._view.receiveTypes"
                :key="item.label"
                :type="item.type"
                :class="['receive-type-tag', item.label === '预留接单' ? 'receive-type-reserved' : 'receive-type-restarted']"
                size="mini"
                effect="plain"
              >
                {{ item.label }}
              </el-tag>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="状态说明" width="195">
          <template slot-scope="scope">
            <span class="status-tip two-line-text" :title="scope.row._view.status.tip">
              {{ scope.row._view.status.tip }}
            </span>
          </template>
        </el-table-column>
        <el-table-column label="佣金" width="120" align="center">
          <template slot-scope="scope">
            <div class="commission-cell">
              <strong>{{ scope.row._view.commission.text }}</strong>
              <small v-if="scope.row._view.commission.note">{{ scope.row._view.commission.note }}</small>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="目标院校专业" min-width="280">
          <template slot-scope="scope">
            <div class="order-info-main">
              <el-tooltip :content="scope.row._view.targetTitle" placement="top" :open-delay="300">
                <strong class="two-line-text">{{ scope.row._view.targetTitle }}</strong>
              </el-tooltip>
              <span>{{ scope.row._view.targetMeta }}</span>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="辅导科目/课时" min-width="225">
          <template slot-scope="scope">
            <div class="tutoring-cell">
              <div>
                <span>科目</span>
                <el-tooltip :content="scope.row._view.subjects" placement="top" :open-delay="300">
                  <p class="two-line-text">{{ scope.row._view.subjects }}</p>
                </el-tooltip>
              </div>
              <div>
                <span>课时</span>
                <p>{{ scope.row._view.lessonHours }}</p>
              </div>
            </div>
          </template>
        </el-table-column>
        <el-table-column label="辅导要求" min-width="260">
          <template slot-scope="scope">
            <div v-if="scope.row._view.demands.length" class="demand-cell">
              <div v-for="(item, index) in scope.row._view.demandPreview" :key="index">
                <p :title="item">{{ item }}</p>
              </div>
              <el-popover v-if="scope.row._view.demands.length > 2" placement="top" width="420" trigger="hover">
                <div class="demand-popover-title">全部辅导要求（{{ scope.row._view.demands.length }}）</div>
                <ul class="demand-list">
                  <li v-for="(item, index) in scope.row._view.demands" :key="index">{{ item }}</li>
                </ul>
                <el-link slot="reference" type="primary" :underline="false" class="demand-more">
                  还有 {{ scope.row._view.demands.length - 2 }} 项，查看全部
                </el-link>
              </el-popover>
            </div>
            <span v-else class="empty-value">无辅导要求</span>
          </template>
        </el-table-column>
        <el-table-column fixed="right" label="沟通记录" width="90" align="center">
          <template slot-scope="scope">
            <el-link type="primary" :underline="false" @click="openRecords(scope.row)">查看</el-link>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-wrap">
        <span>第 {{ pageIndex }} / {{ Math.max(Math.ceil(total / pageSize), 1) }} 页</span>
        <el-pagination
          background
          layout="total, prev, pager, next, sizes, jumper"
          :current-page="pageIndex"
          :page-size="pageSize"
          :page-sizes="[10, 20, 50, 100]"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </el-card>

    <CommunicationRecordsDialog
      :visible="recordsVisible"
      :order="activeOrder"
      :user-id="userId"
      @close="recordsVisible = false"
    />
  </div>
</template>

<script>
import { matchingOrderList } from '@/request/api'
import CommunicationRecordsDialog from './CommunicationRecordsDialog.vue'
import {
  MATCHING_ORDER_STATUS_OPTIONS,
  commissionInfo,
  demandItems,
  formatTime,
  hasValue,
  orderMajor,
  orderReceiveTypeTags,
  orderStatusMeta,
  orderSubjects,
  valueOrDash,
} from './orderFormatters'

export default {
  name: 'MatchingOrder',
  components: { CommunicationRecordsDialog },
  data() {
    return {
      tableData: [],
      total: 0,
      pageIndex: 1,
      pageSize: 10,
      currentStatus: '',
      listLoading: false,
      recordsVisible: false,
      activeOrder: {},
      statusOptions: MATCHING_ORDER_STATUS_OPTIONS,
      headerStyle: { textAlign: 'center', color: '#303133', background: '#f7f8fb', fontWeight: '500' },
      cellStyle: { color: '#303133', fontSize: '13px' },
    }
  },
  computed: {
    companyUser() {
      try {
        return JSON.parse(localStorage.getItem('companyUser') || '{}')
      } catch (error) {
        return {}
      }
    },
    userId() { return this.companyUser.id || localStorage.getItem('userId') || '' },
  },
  created() {
    if (!hasValue(this.userId)) {
      this.$message.warning('未读取到当前登录员工，暂时无法查询本人匹配订单')
      return
    }
    this.getList()
  },
  methods: {
    async getList(resetPage) {
      if (!hasValue(this.userId)) return
      if (resetPage) this.pageIndex = 1
      const params = {
        pageIndex: this.pageIndex,
        pageSize: this.pageSize,
        receiver_company_user_id: this.userId,
      }
      if (hasValue(this.currentStatus)) params.statues = this.currentStatus
      this.listLoading = true
      try {
        const res = await matchingOrderList(params)
        if (res.code === 0) {
          const list = (res.data && res.data.list) || []
          this.tableData = list.map((row) => this.buildOrderView(row))
          this.total = Number((res.data && res.data.count) || 0)
        }
      } finally {
        this.listLoading = false
      }
    },
    handleSizeChange(size) {
      this.pageSize = size
      this.getList(true)
    },
    handleCurrentChange(page) {
      this.pageIndex = page
      this.getList()
    },
    openRecords(row) {
      this.activeOrder = row
      this.recordsVisible = true
    },
    buildOrderView(row) {
      const status = orderStatusMeta(row, this.userId)
      const demands = demandItems(row)
      const college = valueOrDash(row.apply_college_name)
      const major = orderMajor(row)
      const applyYear = hasValue(row.apply_year) ? `${row.apply_year}考研` : '--'
      const academy = valueOrDash(row.apply_academy_name)
      return Object.assign({}, row, {
        _view: {
          orderNo: valueOrDash(row.order_no),
          status,
          receiveTypes: orderReceiveTypeTags(row),
          targetTitle: `${college} ｜ ${major}`,
          targetMeta: `${applyYear} ｜ ${academy}`,
          subjects: orderSubjects(row),
          lessonHours: hasValue(row.lesson_hours) ? `${row.lesson_hours} 小时` : '课时 --',
          demands,
          demandPreview: demands.slice(0, 2),
          commission: commissionInfo(row),
          createTime: formatTime(this.$moment, row.create_time),
        },
      })
    },
    statusTagType(type) { return type === 'primary' ? '' : type },
  },
}
</script>

<style lang="less" scoped>
.matching-order-page { min-height: 100%; }
.breadcrumb { align-items: center; display: flex; height: 30px; margin-bottom: 18px; }
.breadcrumb /deep/ .el-breadcrumb__inner { color: #303133; }
.breadcrumb /deep/ .el-breadcrumb__item:last-child .el-breadcrumb__inner { color: #303133; font-weight: 500; }
.filter-card, .table-card { margin-bottom: 16px; }
.filter-card, .table-card { border-color: #e8eaf0; }
.filter-card { background: #fff; }
.filter-card /deep/ .el-card__body { padding: 14px 20px; }
.filter-row { align-items: center; display: flex; flex-wrap: wrap; gap: 12px; }
.filter-label { color: #303133; font-size: 13px; width: 62px; }
.matching-order-page /deep/ .el-button--primary { background: #59679b; border-color: #59679b; }
.matching-order-page /deep/ .el-button--primary:focus,
.matching-order-page /deep/ .el-button--primary:hover { background: #6977a8; border-color: #6977a8; }
.matching-order-page /deep/ .el-radio-button__orig-radio:checked + .el-radio-button__inner { background: #59679b; border-color: #59679b; box-shadow: -1px 0 0 0 #59679b; }
.matching-order-page /deep/ .el-link.el-link--primary { color: #59679b; }
.matching-order-page /deep/ .el-link.el-link--primary:hover { color: #6977a8; }
.matching-order-page /deep/ .el-pagination.is-background .el-pager li:not(.disabled).active { background: #59679b; }
.table-header, .pagination-wrap { align-items: center; display: flex; justify-content: space-between; }
.table-header { margin-bottom: 14px; }
.card-title { align-items: center; color: #303133; display: flex; font-size: 15px; font-weight: 600; }
.card-title::before { background: #59679b; border-radius: 2px; content: ''; height: 15px; margin-right: 8px; width: 3px; }
.card-title span { color: #303133; font-size: 13px; font-weight: normal; }
.order-table { width: 100%; }
.order-table /deep/ th.el-table__cell { background: #f7f8fb !important; color: #303133 !important; }
.order-table /deep/ .el-table__cell { padding: 11px 0; }
.order-table /deep/ td.el-table__cell, .order-table /deep/ th.el-table__cell { border-color: #e8eaf0; }
.order-table /deep/ .el-table__body tr:hover > td.el-table__cell { background: #f6f7fb; }
.order-table /deep/ .el-table__fixed::before, .order-table /deep/ .el-table__fixed-right::before,
.order-table /deep/ .el-table::before, .order-table /deep/ .el-table::after { background-color: #e8eaf0; }
.status-cell { align-items: center; display: flex; flex-wrap: wrap; gap: 5px; justify-content: center; }
.status-tag { background: #f1f3f9 !important; border-color: #d9deed !important; color: #59679b !important; }
.status-tag.el-tag--danger { background: #fff1f0 !important; border-color: #f5c8c5 !important; color: #d95750 !important; }
.status-tag.el-tag--warning { background: #fff7e8 !important; border-color: #efd6a9 !important; color: #bb7721 !important; }
.status-tag.el-tag--success { background: #eff8f2 !important; border-color: #c7e4d0 !important; color: #438c59 !important; }
.status-tag.el-tag--info { background: #f4f4f5 !important; border-color: #dedfe1 !important; color: #303133 !important; }
.status-tag-received { background: #f1f3f9 !important; border-color: #d9deed !important; color: #59679b !important; }
.receive-type-reserved { background: #eff8f5 !important; border-color: #bfdfd5 !important; color: #328a74 !important; }
.receive-type-restarted { background: #fff7e8 !important; border-color: #efd6a9 !important; color: #b97925 !important; }
.order-identity { align-items: center; display: flex; flex-direction: column; }
.order-number { color: #303133; font-family: Consolas, 'Courier New', monospace; font-weight: 600; }
.order-identity small { color: #303133; font-size: 11px; font-weight: normal; line-height: 17px; margin-top: 4px; white-space: nowrap; }
.status-tip { color: #8a8f98; display: block; font-size: 12px; line-height: 18px; padding: 0 10px; }
.order-info-main, .commission-cell { display: flex; flex-direction: column; }
.order-info-main strong { color: #303133; font-weight: 600; line-height: 21px; }
.order-info-main span { color: #303133; font-size: 12px; line-height: 18px; margin-top: 5px; }
.two-line-text { -webkit-box-orient: vertical; -webkit-line-clamp: 2; display: -webkit-box; overflow: hidden; }
.tutoring-cell, .demand-cell { line-height: 18px; padding: 0 10px; text-align: left; }
.tutoring-cell > div, .demand-cell > div { display: flex; min-width: 0; }
.tutoring-cell > div + div, .demand-cell > div + div { margin-top: 2px; }
.tutoring-cell span { color: #303133; flex: 0 0 34px; font-size: 11px; }
.tutoring-cell p, .demand-cell p { color: #303133; font-size: 12px; margin: 0; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.demand-cell p::before { background: #303133; border-radius: 50%; content: ''; display: inline-block; height: 4px; margin: 0 7px 2px 0; width: 4px; }
.demand-more { font-size: 12px; margin-top: 3px; }
.demand-popover-title { color: #303133; font-weight: 600; margin-bottom: 6px; }
.demand-list { color: #303133; line-height: 22px; margin: 0; max-height: 320px; overflow-y: auto; padding-left: 20px; }
.empty-value { color: #303133; }
.commission-cell strong { color: #ef5b53; font-size: 16px; }
.commission-cell small { color: #d98b28; line-height: 17px; margin-top: 3px; }
.pagination-wrap { color: #303133; font-size: 13px; margin-top: 16px; }
@media screen and (max-width: 900px) {
  .filter-label { width: 100%; }
}
</style>
